import React, { useState, useEffect } from "react";
import Card, { CardBody } from "@/components/common/Card";
import ConfirmDialog, { DeleteIcon } from "@/components/common/ConfirmDialog";
import { generarResumenHorario } from "@/utils/horarioUtils";
import {
  FaBuilding,
  FaPhone,
  FaMapMarkerAlt,
  FaCity,
  FaClock,
  FaPen,
  FaTrash,
  FaUsers,
} from "react-icons/fa";

/**
 * Componente para mostrar una sucursal individual
 * Versión móvil exactamente como la imagen 1 (ClienteItem)
 * Versión escritorio con botones pequeños horizontales a la derecha
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.sucursal - Datos de la sucursal
 * @param {function} props.onEdit - Función para editar la sucursal
 * @param {function} props.onDelete - Función para eliminar la sucursal
 * @returns {JSX.Element} - Componente de ítem de sucursal
 */
const SucursalItem = ({ sucursal, onEdit, onDelete }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Pantallas menores a 768px se consideran móviles
    };

    // Comprobar inicialmente
    checkMobile();

    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener("resize", checkMobile);

    // Limpiar al desmontar
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <>
      <Card
        hover
        className="mb-3 border-l-4 border-blue-500 transition-all duration-300 hover:border-blue-400 w-full"
      >
        <CardBody className="p-0">
          {isMobile ? (
            <div>
              {/* Versión móvil - exactamente como en la imagen 1 */}
              <div className="p-4">
                <div className="flex items-center mb-2">
                  <div className="mr-2 bg-blue-600 bg-opacity-20 p-2 rounded-full">
                    <FaBuilding className="text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold">{sucursal.Nombre}</h3>
                </div>
                <div className="text-sm text-gray-300 mt-2">
                  <div className="grid grid-cols-2 gap-2">
                    {sucursal.Telefono && (
                      <div className="flex items-center mb-1">
                        <FaPhone className="mr-2 text-gray-500" />
                        <span>{sucursal.Telefono}</span>
                      </div>
                    )}
                    <div className="flex items-center mb-1">
                      <FaUsers className="mr-2 text-gray-500" />
                      <span>{sucursal.CapacidadClientes || 1} clientes</span>
                    </div>
                    {sucursal.Ciudad && (
                      <div className="flex items-center mb-1">
                        <FaCity className="mr-2 text-gray-500" />
                        <span>{sucursal.Ciudad}</span>
                      </div>
                    )}
                    {sucursal.HorarioAtencion && (
                      <div className="flex items-center mb-1">
                        <FaClock className="mr-2 text-gray-500" />
                        <span>{generarResumenHorario(sucursal.HorarioAtencion)}</span>
                      </div>
                    )}
                    {sucursal.Direccion && (
                      <div className="flex items-center mb-1 col-span-2">
                        <FaMapMarkerAlt className="mr-2 text-gray-500" />
                        <span>{sucursal.Direccion}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-between border-t border-gray-700 bg-gray-800">
                <button
                  onClick={() => onEdit(sucursal)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                  title="Editar"
                >
                  <FaPen className="text-lg" />
                  <span className="text-xs mt-1">Editar</span>
                </button>
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                  title="Eliminar"
                >
                  <FaTrash className="text-lg" />
                  <span className="text-xs mt-1">Eliminar</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center p-3">
              {/* Versión escritorio - sin contenedor de fondo */}
              {/* Nombre de la sucursal a la izquierda */}
              <div className="w-1/4 flex items-center">
                <div className="flex items-center">
                  <FaBuilding className="text-blue-400 mr-2" />
                  <h3 className="font-semibold text-white">{sucursal.Nombre}</h3>
                </div>
              </div>
              
              {/* Datos a la derecha en 2 filas */}
              <div className="w-2/3">
                {/* Primera fila: teléfono, horario y capacidad */}
                <div className="grid grid-cols-3 gap-4 mb-2">
                  <div className="text-sm text-gray-300">
                    <div className="flex items-center">
                      <FaPhone className="text-gray-500 mr-2" />
                      <span>{sucursal.Telefono || "Sin teléfono"}</span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-300">
                    <div className="flex items-center">
                      <FaClock className="text-gray-500 mr-2" />
                      <span>{generarResumenHorario(sucursal.HorarioAtencion) || "Sin horario"}</span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-300">
                    <div className="flex items-center">
                      <FaUsers className="text-gray-500 mr-2" />
                      <span>{sucursal.CapacidadClientes || 1} clientes</span>
                    </div>
                  </div>
                </div>
                
                {/* Segunda fila: Dirección y Ciudad */}
                <div className="text-sm text-gray-300">
                  <div className="flex items-center">
                    <FaMapMarkerAlt className="text-gray-500 mr-2" />
                    <span>{sucursal.Direccion || "Sin dirección"}</span>
                    {sucursal.Ciudad && (
                      <>
                        <span className="mx-2 text-gray-600">•</span>
                        <FaCity className="text-gray-500 mr-1" />
                        <span>{sucursal.Ciudad}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 ml-2 self-center">
                <button
                  onClick={() => onEdit(sucursal)}
                  className="text-white bg-yellow-500 hover:bg-yellow-600 p-1.5 rounded w-8 h-8 flex items-center justify-center"
                  title="Editar"
                >
                  <FaPen size={14} />
                </button>
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-white bg-red-500 hover:bg-red-600 p-1.5 rounded w-8 h-8 flex items-center justify-center"
                  title="Eliminar"
                >
                  <FaTrash size={14} />
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          onDelete(sucursal.id);
          setIsDeleteDialogOpen(false);
        }}
        title="Eliminar sucursal"
        message={`¿Estás seguro de que deseas eliminar la sucursal "${sucursal.Nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmVariant="danger"
        icon={<DeleteIcon />}
      />
    </>
  );
};

export default SucursalItem;
